import React from "react";
import { getRankKey, classifyRisk } from "../actions/actions";
import Text from "../components/text";

const SelectedList = ({
  selectedColleges,
  removeCollege,
  moveCollegeUp,
  moveCollegeDown,
  sortSelectedCollegesByRank,
  formData,
  isRankSorted,
  setIsRankSorted,
  originalOrder,
  setOriginalOrder,
  setIsSaved,
  isFullScreen,
}) => {
  const rankKey = getRankKey(formData);
  const listContainerClass = [
    "laptop-view1",
    "overflow-y-auto",
    "border",
    "rounded-lg",
    isFullScreen
      ? "h-[100dvh] min-h-[100dvh] max-h-[100dvh] full-height-block"
      : "max-h-[calc(80vh-160px)]", // Only apply when not full-screen
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={listContainerClass + " w-full flex flex-col"}>
      {/* Table container */}
      {selectedColleges.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg text-center h-full flex flex-col items-center justify-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-12 w-12 mx-auto text-gray-400 mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
            />
          </svg>
          <h3 className="text-lg font-medium text-gray-700 mb-2">
            {Text.SelectedList.NoCollegesSelected}
          </h3>
          <p className="text-gray-500 max-w-xs mx-auto">
            {Text.SelectedList.NoCollegesSelectedDescription}
          </p>
        </div>
      ) : (
        <table className="w-full divide-y divide-gray-200">
          <thead className="bg-gray-100 sticky top-0 z-30">
            <tr>
              <th className="py-3 pl-4 pr-2 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">
                College{" "}
                <span className="text-blue ms-1">
                  ({selectedColleges.length})
                </span>
              </th>
              <th className="py-3 px-2 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">
                <div
                  onClick={sortSelectedCollegesByRank}
                  className="flex items-center cursor-pointer group"
                  title={isRankSorted ? "Sorted by Rank" : "Sort by Rank"}
                >
                  <span>Rank</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 ml-1.5 ${
                      isRankSorted
                        ? "text-blue-600"
                        : "text-gray-500 group-hover:text-gray-700"
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"
                    />
                  </svg>
                </div>
              </th>
              <th className="py-3 px-2 text-center text-sm font-semibold text-gray-700 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {selectedColleges.map((college, index) => {
              const collegeRank = parseInt(college[rankKey]) || 0;
              const studentRank = parseInt(formData.rank) || 0;
              const riskClass = classifyRisk(studentRank, collegeRank);

              const riskBgClass =
                riskClass === "safety-safe"
                  ? "bg-green-50"
                  : riskClass === "safety-moderate"
                  ? "bg-yellow-50"
                  : riskClass === "safety-ambitious"
                  ? "bg-red-50"
                  : "";

              const riskIndicator =
                riskClass === "safety-safe"
                  ? "bg-green-400"
                  : riskClass === "safety-moderate"
                  ? "bg-yellow-400"
                  : "bg-red-400";

              return (
                <tr
                  key={index}
                  className={`${riskBgClass} hover:bg-gray-50 transition-colors`}
                >
                  {/* College column with mobile layout */}
                  <td className="py-3 pl-4 pr-2">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-1 self-stretch">
                        <div
                          className={`w-1 h-full rounded ${riskIndicator}`}
                        ></div>
                      </div>
                      <div className="ml-3 min-w-0 flex-1">
                        <div className="flex items-center mb-1">
                          <span className="font-mono text-xs text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">
                            {college.inst_code}
                          </span>
                          <span className="text-xs text-gray-500 ml-2">
                            {" - "}
                            {college.place}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-gray-900 line-clamp-2">
                          {college.inst_name}
                        </p>
                      </div>
                    </div>
                  </td>

                  {/* Rank and Branch combined for mobile */}
                  <td className="py-3 px-2">
                    <div className="font-medium text-sm text-gray-900 mb-1">
                      {college[rankKey]}
                    </div>
                    <span className="text-xs py-0.5 px-1.5 bg-blue-100 text-blue-800 rounded inline-block max-w-full truncate">
                      {college.branch_code}
                    </span>
                  </td>

                  {/* Actions column */}
                  <td className="py-3 px-2 text-center">
                    <div className="flex justify-end space-x-1">
                      <button
                        onClick={() => moveCollegeUp(index)}
                        disabled={index === 0}
                        className={`p-1.5 rounded-full ${
                          index === 0
                            ? "text-gray-400 cursor-not-allowed"
                            : "text-blue-600 hover:bg-blue-100"
                        }`}
                        title="Move up"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 15l7-7 7 7"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => moveCollegeDown(index)}
                        disabled={index === selectedColleges.length - 1}
                        className={`p-1.5 rounded-full ${
                          index === selectedColleges.length - 1
                            ? "text-gray-400 cursor-not-allowed"
                            : "text-blue-600 hover:bg-blue-100"
                        }`}
                        title="Move down"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => removeCollege(college)}
                        className="p-1.5 text-red-600 hover:bg-red-100 rounded-full"
                        title="Remove"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default SelectedList;
